import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/api';
import { Card, Btn, Input, PageHeader } from '../components/UI';

const TABS = ['hero', 'mission', 'stats', 'values', 'team', 'company'];

const Textarea = ({ label, value, onChange, rows = 3, placeholder = '' }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>{label}</label>}
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
      style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
  </div>
);

export const AboutPage = () => {
  const [about,     setAbout]     = useState(null);
  const [tab,       setTab]       = useState('hero');
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState({ text: '', ok: true });

  useEffect(() => { adminApi.getAbout().then(setAbout).catch(() => {}); }, []);

  const setField = (path, value) => {
    setAbout(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) { obj = obj[keys[i]]; }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setArrItem = (arr, idx, key, value) => {
    setAbout(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next[arr][idx][key] = value;
      return next;
    });
  };

  const addArrItem = (arr, template) => {
    setAbout(prev => ({ ...prev, [arr]: [...(prev[arr] || []), template] }));
  };

  const removeArrItem = (arr, idx) => {
    setAbout(prev => ({ ...prev, [arr]: prev[arr].filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true); setMsg({ text: '', ok: true });
    try {
      await adminApi.updateAbout(about);
      setMsg({ text: '✅ Saved successfully!', ok: true });
      setTimeout(() => setMsg({ text: '', ok: true }), 3000);
    } catch (e) {
      setMsg({ text: `❌ ${e.message}`, ok: false });
    } finally { setSaving(false); }
  };

  if (!about) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#64748B', fontSize: 14 }}>
      Loading About page data…
    </div>
  );

  return (
    <div>
      <PageHeader
        title="About Us Page"
        subtitle="Edit content displayed on the public About Us page"
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {msg.text && <span style={{ fontSize: 13, color: msg.ok ? '#15803D' : '#DC2626' }}>{msg.text}</span>}
            <Btn color="blue" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Btn>
          </div>
        }
      />

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E2E8F0', marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            border: 'none', background: 'none', padding: '9px 18px', fontSize: 13,
            fontWeight: tab === t ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit',
            color: tab === t ? '#EF1D28' : '#64748B',
            borderBottom: `2px solid ${tab === t ? '#EF1D28' : 'transparent'}`,
            textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>

      <Card>
        {/* Hero */}
        {tab === 'hero' && (
          <div>
            <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 16, fontSize: 15 }}>Hero Section</div>
            <Input label="Badge Text" value={about.hero?.badge || ''} onChange={v => setField('hero.badge', v)} placeholder="Our Story" />
            <Input label="Main Title" value={about.hero?.title || ''} onChange={v => setField('hero.title', v)} />
            <Input label="Subtitle" value={about.hero?.subtitle || ''} onChange={v => setField('hero.subtitle', v)} />
            <Textarea label="Description" value={about.hero?.description || ''} onChange={v => setField('hero.description', v)} rows={4} />
          </div>
        )}

        {/* Mission */}
        {tab === 'mission' && (
          <div>
            <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 16, fontSize: 15 }}>Mission Section</div>
            <Input label="Mission Title" value={about.mission?.title || ''} onChange={v => setField('mission.title', v)} />
            <Textarea label="Mission Description" value={about.mission?.description || ''} onChange={v => setField('mission.description', v)} rows={5} />
          </div>
        )}

        {/* Stats */}
        {tab === 'stats' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 15 }}>Stats</div>
              <Btn color="ghost" size="sm" onClick={() => addArrItem('stats', { value: '', label: '', description: '' })}>+ Add Stat</Btn>
            </div>
            {(about.stats || []).map((stat, i) => (
              <div key={i} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Stat #{i + 1}</span>
                  <button onClick={() => removeArrItem('stats', i)} style={{ background: '#FEF2F2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input label="Value" value={stat.value || ''} onChange={v => setArrItem('stats', i, 'value', v)} placeholder="50,000+" />
                  <Input label="Label" value={stat.label || ''} onChange={v => setArrItem('stats', i, 'label', v)} placeholder="Resumes Created" />
                </div>
                <Input label="Description" value={stat.description || ''} onChange={v => setArrItem('stats', i, 'description', v)} placeholder="Short supporting text" />
              </div>
            ))}
          </div>
        )}

        {/* Values */}
        {tab === 'values' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 15 }}>Our Values</div>
              <Btn color="ghost" size="sm" onClick={() => addArrItem('values', { icon: '⭐', title: '', description: '' })}>+ Add Value</Btn>
            </div>
            {(about.values || []).map((val, i) => (
              <div key={i} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Value #{i + 1}</span>
                  <button onClick={() => removeArrItem('values', i)} style={{ background: '#FEF2F2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12 }}>
                  <Input label="Icon" value={val.icon || ''} onChange={v => setArrItem('values', i, 'icon', v)} placeholder="🎯" />
                  <Input label="Title" value={val.title || ''} onChange={v => setArrItem('values', i, 'title', v)} />
                </div>
                <Textarea label="Description" value={val.description || ''} onChange={v => setArrItem('values', i, 'description', v)} rows={2} />
              </div>
            ))}
          </div>
        )}

        {/* Team */}
        {tab === 'team' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 15 }}>Team Members</div>
              <Btn color="ghost" size="sm" onClick={() => addArrItem('team', { name: '', role: '', bio: '', avatar: '', linkedin: '' })}>+ Add Member</Btn>
            </div>
            {(about.team || []).map((member, i) => (
              <div key={i} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Member #{i + 1}</span>
                  <button onClick={() => removeArrItem('team', i)} style={{ background: '#FEF2F2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input label="Name" value={member.name || ''} onChange={v => setArrItem('team', i, 'name', v)} />
                  <Input label="Role / Title" value={member.role || ''} onChange={v => setArrItem('team', i, 'role', v)} placeholder="Co-Founder & CEO" />
                </div>
                <Textarea label="Bio" value={member.bio || ''} onChange={v => setArrItem('team', i, 'bio', v)} rows={2} />
                <Input label="Avatar URL" value={member.avatar || ''} onChange={v => setArrItem('team', i, 'avatar', v)} placeholder="https://..." />
                <Input label="LinkedIn URL" value={member.linkedin || ''} onChange={v => setArrItem('team', i, 'linkedin', v)} placeholder="https://linkedin.com/in/..." />
              </div>
            ))}
          </div>
        )}

        {/* Company */}
        {tab === 'company' && (
          <div>
            <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 16, fontSize: 15 }}>Company Info</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Company Name" value={about.companyName || ''} onChange={v => setField('companyName', v)} />
              <Input label="Founded Year" value={String(about.foundedYear || '')} onChange={v => setField('foundedYear', Number(v) || v)} />
              <Input label="Location" value={about.location || ''} onChange={v => setField('location', v)} placeholder="San Francisco, CA" />
              <Input label="Contact Email" value={about.email || ''} onChange={v => setField('email', v)} placeholder="hello@company.com" />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
